import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../../config/api';

const RescheduleBooking = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('id');
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingReschedule, setLoadingReschedule] = useState(false);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  // Load booking details
  useEffect(() => {
    loadBookingDetails();
  }, [token]);

  const loadBookingDetails = async () => {
    if (!token) {
      setError({
        message: 'Invalid or missing token',
        status: 400
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/planify/booking/public/booking-by-token/${token}`);
      
      if (response.data) {
        setBookingData(response.data);
        // Set current date and time as default
        const currentStart = new Date(response.data.scheduled_start);
        const currentDate = currentStart.toISOString().split('T')[0];
        setSelectedDate(currentDate);
        setSelectedTime(`${currentStart.toTimeString().split(' ')[0]} - ${new Date(response.data.scheduled_end).toTimeString().split(' ')[0]}`);
      }
    } catch (err) {
      console.error('Error loading booking details:', err);
      setError({
        message: err.response?.data?.message || 'Failed to load booking details',
        status: err.response?.status || 500
      });
    } finally {
      setLoading(false);
    }
  };

  // Load free slots for selected date
  const loadFreeSlots = async (date) => {
    if (!bookingData?.user_id) return;
    
    try {
      setLoadingSlots(true);
      setError(null);
      
      const dateObj = new Date(date);
      const weekDay = (dateObj.getDay() + 6) % 7; // 0=Monday, 1=Tuesday, ..., 6=Sunday
      
      const response = await api.get(`/planify/event/public/user/${bookingData.user_id}/free-slot`, {
        params: {
          date: date,
          week_day: weekDay
        }
      });

      if (response.data && Array.isArray(response.data)) {
        const formattedSlots = response.data.map(slot => {
          const startTime = new Date(slot.start_time);
          const endTime = new Date(slot.end_time);
          
          const formatTime = (date) => {
            return date.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          };
          
          return {
            id: slot.availability_id,
            start_time: slot.start_time,
            end_time: slot.end_time,
            display_time: `${formatTime(startTime)} - ${formatTime(endTime)}`,
            start_display: formatTime(startTime),
            end_display: formatTime(endTime)
          };
        });
        
        setAvailableSlots(formattedSlots);
      } else {
        setAvailableSlots([]);
      }
    } catch (err) {
      console.error('Error loading free slots:', err);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Handle date selection
  const handleDateSelection = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    loadFreeSlots(date);
  };

  // Handle time selection
  const handleTimeSelection = (time) => {
    setSelectedTime(time);
  };

  // Generate next 30 days
  const getNext30Days = () => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      dates.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        }),
        dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'long' })
      });
    }
    return dates;
  };

  // Handle reschedule
  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      setModalType('error');
      setModalTitle('Selection Required');
      setModalMessage('Please select both date and time for rescheduling.');
      setShowModal(true);
      return;
    }

    try {
      setLoadingReschedule(true);
      
      const selectedSlot = availableSlots.find(slot => slot.display_time === selectedTime);
      if (!selectedSlot) {
        throw new Error('Selected time slot not found');
      }

      // Convert ISO datetime to API time format (HH:MM:SS.sssZ)
      const formatTimeForAPI = (isoDateTime) => {
        const date = new Date(isoDateTime);
        const timeString = date.toTimeString().split(' ')[0]; // HH:MM:SS
        return `${timeString}.000Z`; // Convert to HH:MM:SS.sssZ format
      };

      const reschedulePayload = {
        booking_data: {
          availability_id: selectedSlot.id,
          booked_by_email: bookingData.invitee_email,
          booked_by_name: bookingData.invitee_name,
          custom_fields: bookingData.custom_fields,
          customer_id: bookingData.customer_id,
          end_date: selectedDate,
          end_time: formatTimeForAPI(selectedSlot.end_time),
          event_type_id: bookingData.id,
          mobile: bookingData.mobile,
          note: bookingData.note,
          start_date: selectedDate,
          start_time: formatTimeForAPI(selectedSlot.start_time),
          timezone: "Asia/Kolkata"
        }
      };

      const response = await api.post('/planify/booking/public/reschedule', reschedulePayload, {
        params: { token: token }
      });
      
      if (response.data && response.data.old_booking_id && response.data.new_booking_id) {
        setModalType('success');
        setModalTitle('Reschedule Successful!');
        setModalMessage(`Your appointment has been rescheduled successfully. Old booking ID: ${response.data.old_booking_id}, New booking ID: ${response.data.new_booking_id}. You will receive a confirmation email shortly.`);
        setShowModal(true);
      } else {
        setModalType('error');
        setModalTitle('Reschedule Failed');
        setModalMessage(response.data?.message || 'Reschedule failed. Please try again.');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Reschedule error:', error);
      setModalType('error');
      setModalTitle('Reschedule Failed');
      setModalMessage(error.response?.data?.message || error.message || 'Please try again');
      setShowModal(true);
    } finally {
      setLoadingReschedule(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Booking</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Not Found</h2>
          <p className="text-gray-600">The booking you're looking for doesn't exist or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-6 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold">Reschedule Appointment</h1>
          <p className="text-blue-100 mt-2">Select a new date and time for your appointment</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Current Booking Details */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Appointment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Provider</p>
              <p className="font-medium">{bookingData.host_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Current Date & Time</p>
              <p className="font-medium">
                {new Date(bookingData.scheduled_start).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })} at {new Date(bookingData.scheduled_start).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Duration</p>
              <p className="font-medium">{bookingData.duration_minutes} minutes</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-medium">
                {bookingData.location_type === 3 ? 'In-Person' : 'Online'}
              </p>
            </div>
          </div>
        </div>

        {/* Reschedule Form */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Select New Date & Time</h2>
          
          {/* Date Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select New Date <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-7 gap-2 mb-4">
              {getNext30Days().map((date) => (
                <button
                  key={date.value}
                  onClick={() => handleDateSelection(date.value)}
                  className={`p-3 text-center rounded-lg border transition-all ${
                    selectedDate === date.value
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white hover:bg-gray-50 border-gray-300'
                  }`}
                >
                  <p className="text-xs">{date.label.split(',')[0]}</p>
                  <p className="font-semibold text-sm">{date.label.split(' ')[1]}</p>
                  {selectedDate === date.value && (
                    <p className="text-xs mt-1 opacity-75">Selected</p>
                  )}
                </button>
              ))}
            </div>
            {selectedDate && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Selected Date:</span> {
                    new Date(selectedDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  }
                </p>
              </div>
            )}
          </div>

          {/* Time Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select New Time <span className="text-red-500">*</span>
            </label>
            {loadingSlots ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600">Loading available slots...</span>
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {availableSlots.map((slot, index) => (
                  <button
                    key={slot.id || index}
                    onClick={() => handleTimeSelection(slot.display_time)}
                    className={`px-4 py-3 text-sm rounded-lg border transition-all ${
                      selectedTime === slot.display_time
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white hover:bg-gray-50 border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <p className="font-medium">{slot.start_display}</p>
                      <p className="text-xs opacity-75">to {slot.end_display}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : selectedDate ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-3">No available slots for this date</p>
                <p className="text-sm text-gray-400">Please select a different date</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">Please select a date first</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleReschedule}
              disabled={!selectedDate || !selectedTime || loadingReschedule}
              className="flex-1 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loadingReschedule ? 'Rescheduling...' : 'Confirm Reschedule'}
            </button>
            <button
              onClick={() => navigate('/booking/find-provider')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Success/Error Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-center mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  modalType === 'success' 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-red-100 text-red-600'
                }`}>
                  {modalType === 'success' ? (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Modal Content */}
              <div className="text-center">
                <h3 className={`text-xl font-semibold mb-2 ${
                  modalType === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {modalTitle}
                </h3>
                <p className="text-gray-600 mb-6">
                  {modalMessage}
                </p>

                {/* Modal Actions */}
                <button
                  onClick={() => {
                    setShowModal(false);
                    if (modalType === 'success') {
                      navigate('/booking/find-provider');
                    }
                  }}
                  className={`w-full px-4 py-2 rounded-lg transition-colors ${
                    modalType === 'success' 
                      ? 'bg-blue-500 text-white hover:bg-blue-600' 
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  {modalType === 'success' ? 'Close' : 'Try Again'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RescheduleBooking;
